import React from "react";
import { Box, Typography, Grow } from "@mui/material";
import { colors, gradient, typography as themeTypography } from "../theme/designTheme";

const TOOLTIP_WIDTH = 300;
const TOOLTIP_HEIGHT = 110;

const parseRows = (text) =>
  text
    .split("\n")
    .map((line) => {
      const idx = line.indexOf(":");
      if (idx === -1) return { label: null, value: line.trim() };
      return {
        label: line.slice(0, idx).trim(),
        value: line.slice(idx + 1).trim(),
      };
    })
    .filter((r) => r.value || r.label);

const Tooltip = ({ data }) => {
  if (!data || !data.text) return null;

  const { text, position } = data;
  const rows = parseRows(text);
  const [headline, ...details] = rows;

  const viewportWidth  = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const OFFSET = 18;
  let left = position.x + OFFSET;
  let top  = position.y + OFFSET;
  let caret = "top-left";

  if (left + TOOLTIP_WIDTH > viewportWidth) {
    left = position.x - TOOLTIP_WIDTH - OFFSET;
    caret = "top-right";
  }
  if (top + TOOLTIP_HEIGHT > viewportHeight) {
    top = position.y - TOOLTIP_HEIGHT - OFFSET;
    caret = caret === "top-right" ? "bottom-right" : "bottom-left";
  }
  if (left < 0) left = 10;
  if (top  < 0) top  = 10;

  const caretStyles = {
    "top-left":     { top: -5,  left: 14,  right: "auto", bottom: "auto" },
    "top-right":    { top: -5,  right: 14, left: "auto",  bottom: "auto" },
    "bottom-left":  { bottom: -5, left: 14, right: "auto", top: "auto"   },
    "bottom-right": { bottom: -5, right: 14, left: "auto", top: "auto"   },
  }[caret];

  return (
    <Grow in={!!text} timeout={160} style={{ transformOrigin: "top left" }}>
      <Box
        sx={{
          position: "absolute",
          top: `${top}px`,
          left: `${left}px`,
          zIndex: 1000,
          minWidth: 190,
          maxWidth: TOOLTIP_WIDTH,
          pointerEvents: "none",
          borderRadius: "12px",
          background: gradient.insight,
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.08) inset, 0 12px 32px rgba(10, 42, 90, 0.32), 0 2px 6px rgba(10, 42, 90, 0.18)",
          border: "1px solid rgba(255,255,255,0.10)",
          overflow: "visible",
        }}
      >
        {/* Caret */}
        <Box
          sx={{
            position: "absolute",
            width: 10,
            height: 10,
            background: gradient.insight,
            borderTop: "1px solid rgba(255,255,255,0.10)",
            borderLeft: "1px solid rgba(255,255,255,0.10)",
            transform:
              caret.startsWith("top")
                ? "rotate(45deg)"
                : "rotate(-135deg)",
            ...caretStyles,
          }}
        />

        {/* Soft sheen overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "12px",
            background:
              "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.12), transparent 55%)",
            pointerEvents: "none",
          }}
        />

        <Box sx={{ position: "relative", px: 1.75, py: 1.25 }}>
          {headline && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.25, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: themeTypography.fontFamily,
                  fontSize: "9px",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  flexShrink: 0,
                }}
              >
                {headline.label || "Detail"}
              </Typography>

              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1,
                  py: 0.4,
                  borderRadius: "999px",
                  bgcolor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  backdropFilter: "blur(6px)",
                  minWidth: 0,
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: "#7ab8ff",
                    boxShadow: "0 0 8px rgba(122, 184, 255, 0.9)",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: themeTypography.fontFamilyDisplay || themeTypography.fontFamily,
                    fontSize: "11.5px",
                    fontWeight: 700,
                    color: "#ffffff",
                    letterSpacing: "-0.005em",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}
                >
                  {headline.value}
                </Typography>
              </Box>
            </Box>
          )}

          {details.length > 0 && (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: "1px solid rgba(255,255,255,0.12)",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              {details.map((row, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}
                >
                  <Typography
                    sx={{
                      fontFamily: themeTypography.fontFamily,
                      fontSize: "10px",
                      fontWeight: 600,
                      color: "rgba(255,255,255,0.62)",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: themeTypography.fontFamily,
                      fontVariantNumeric: "tabular-nums",
                      fontSize: "11.5px",
                      fontWeight: 600,
                      color: "#ffffff",
                      textAlign: "right",
                    }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Grow>
  );
};

export default Tooltip;